import React from "react";

import { Audio, Catalogos, Cola, Destino, Extension, Horario, Ivr } from "../../../../services/v2/telefonia.service";

export const errorTexto = (e: any): string => e?.response?.data?.message || e?.message || "Algo falló. Intenta otra vez.";

/** "2:35" / "1:02:10" a partir de segundos. */
export const seg = (s: number | null | undefined): string => {
	const n = Math.max(0, Math.round(Number(s) || 0));
	const h = Math.floor(n / 3600), m = Math.floor((n % 3600) / 60), r = n % 60;
	return (h ? h + ":" + String(m).padStart(2, "0") : String(m)) + ":" + String(r).padStart(2, "0");
};
export const minutos = (s: number | null | undefined): string => {
	const n = Math.round((Number(s) || 0) / 60);
	return n >= 60 ? (n / 60).toFixed(1).replace(".0", "") + " h" : n + " min";
};
export const num = (v: any): string => Number(v || 0).toLocaleString("es-MX");
export const pct = (a: any, b: any): string => (Number(b) ? Math.round((100 * Number(a)) / Number(b)) + "%" : "–");

const utc = (s: string) => new Date(s.includes("T") || s.endsWith("Z") ? s : s.replace(" ", "T") + "Z");
/** "8 oct, 3:24 pm" en la hora del navegador. */
export const cuando = (s: string | null | undefined, conFecha = true): string => {
	if (!s) return "";
	const d = utc(s);
	const hora = d.toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit", hour12: true }).replace(/p\.\s?m\./, "pm").replace(/a\.\s?m\./, "am");
	if (!conFecha) return hora;
	const hoy = new Date();
	const mismo = d.toDateString() === hoy.toDateString();
	return (mismo ? "Hoy" : d.toLocaleDateString("es-MX", { day: "numeric", month: "short" }).replace(".", "")) + ", " + hora;
};
export const hace = (s: string | null | undefined): string => {
	if (!s) return "nunca";
	const m = Math.max(0, Math.round((Date.now() - utc(s).getTime()) / 60000));
	if (m < 1) return "ahora";
	if (m < 60) return "hace " + m + " min";
	const h = Math.round(m / 60);
	return h < 24 ? "hace " + h + " h" : "hace " + Math.round(h / 24) + " d";
};

export const telBonito = (n: string | null | undefined): string => {
	const d = String(n || "").replace(/\D/g, "");
	if (/^504\d{8}$/.test(d)) return "+504 " + d.slice(3, 7) + " " + d.slice(7);
	if (/^52\d{10}$/.test(d)) return "+52 " + d.slice(2, 4) + " " + d.slice(4, 8) + " " + d.slice(8);
	if (/^503\d{8}$/.test(d)) return "+503 " + d.slice(3, 7) + " " + d.slice(7);
	return d ? "+" + d : "";
};

export const DIR_TXT: Record<string, string> = { in: "Entrante", out: "Saliente", interna: "Interna" };
export const DISP_TXT: Record<string, string> = { contestada: "Contestada", no_contesto: "No contestó", ocupado: "Ocupado", fallo: "Falló", abandonada: "Colgó esperando", vencida: "Nadie contestó" };
export const DISP_CLASE: Record<string, string> = { contestada: "ok", no_contesto: "", ocupado: "", fallo: "oro", abandonada: "oro", vencida: "oro" };

export const plantelNombre = (cat: Catalogos | null, id: number | null | undefined): string =>
	id === null || id === undefined ? "Todos" : cat?.planteles.find((p) => p.id === Number(id))?.name || "Plantel " + id;

export const Aviso: React.FC<{ texto: string | null; tipo?: "oro" | "ok"; onClose?: () => void }> = ({ texto, tipo = "oro", onClose }) =>
	texto ? <div className={"nu-aviso " + tipo} onClick={onClose} role="alert">{texto}</div> : null;

interface DestinoProps {
	value: Destino | null | undefined; onChange: (d: Destino | null) => void; colas: Cola[]; ivrs: Ivr[]; extensiones: Extension[]; audios: Audio[]; equipos: string[];
	permitirVacio?: boolean; vacioTexto?: string; sin?: string[];
}
/** A dónde va una llamada: departamento, recepcionista, extensión, equipo, audio y colgar, o colgar. */
export const DestinoSelect: React.FC<DestinoProps> = ({ value, onChange, colas, ivrs, extensiones, audios, equipos, permitirVacio, vacioTexto, sin = [] }) => {
	const type = value?.type || "";
	const target = value?.target || "";
	const tipos: [string, string][] = [["queue", "Departamento"], ["ivr", "Recepcionista"], ["extension", "Extensión"], ["team", "Equipo"], ["audio", "Audio y colgar"], ["hangup", "Colgar"]];
	const setType = (t: string) => {
		if (!t) return onChange(null);
		const primero = t === "queue" ? colas[0]?.code : t === "ivr" ? String(ivrs[0]?.id || "") : t === "extension" ? extensiones[0]?.extension : t === "team" ? equipos[0] : t === "audio" ? String(audios[0]?.id || "") : "";
		onChange({ type: t as any, target: primero || "" });
	};
	return (
		<div className="tf-destino">
			<select value={type} onChange={(e) => setType(e.target.value)} aria-label="Tipo de destino">
				{permitirVacio ? <option value="">{vacioTexto || "— nada —"}</option> : null}
				{tipos.filter(([t]) => !sin.includes(t)).map(([t, n]) => <option key={t} value={t}>{n}</option>)}
			</select>
			{type === "queue" ? <select value={target} onChange={(e) => onChange({ type: "queue", target: e.target.value })} aria-label="Departamento">{colas.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}</select> : null}
			{type === "ivr" ? <select value={target} onChange={(e) => onChange({ type: "ivr", target: e.target.value })} aria-label="Recepcionista">{ivrs.map((i) => <option key={i.id} value={String(i.id)}>{i.name}</option>)}</select> : null}
			{type === "extension" ? <select value={target} onChange={(e) => onChange({ type: "extension", target: e.target.value })} aria-label="Extensión">{extensiones.map((x) => <option key={x.extension} value={x.extension}>{x.extension} · {x.name}</option>)}</select> : null}
			{type === "team" ? <select value={target} onChange={(e) => onChange({ type: "team", target: e.target.value })} aria-label="Equipo">{equipos.map((t) => <option key={t} value={t}>{t}</option>)}</select> : null}
			{type === "audio" ? <select value={target} onChange={(e) => onChange({ type: "audio", target: e.target.value })} aria-label="Audio">{audios.map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}</select> : null}
		</div>
	);
};

export const destinoTexto = (d: Destino | null | undefined, colas: Cola[], ivrs: Ivr[], extensiones: Extension[], audios: Audio[]): string => {
	if (!d || !d.type) return "—";
	if (d.type === "queue") return "Departamento " + (colas.find((c) => c.code === d.target)?.name || d.target);
	if (d.type === "ivr") return "Recepcionista " + (ivrs.find((i) => String(i.id) === String(d.target))?.name || d.target);
	if (d.type === "extension") return "Ext. " + d.target + " " + (extensiones.find((x) => x.extension === d.target)?.name || "");
	if (d.type === "team") return "Equipo " + d.target;
	if (d.type === "audio") return "Audio " + (audios.find((a) => String(a.id) === String(d.target))?.name || d.target) + " y colgar";
	return "Colgar";
};

const DIAS_CORTOS = [[1, "L"], [2, "M"], [3, "Mi"], [4, "J"], [5, "V"], [6, "S"], [7, "D"]] as [number, string][];
/** Horario de atención: zona y, por día, uno o dos rangos. Sin horario = siempre abierto. */
export const HorarioEditor: React.FC<{ value: Horario | null; onChange: (h: Horario | null) => void; zonas: Record<string, string> }> = ({ value, onChange, zonas }) => {
	const h: Horario = value || { zona: Object.keys(zonas)[0] || "America/Tegucigalpa", dias: {} };
	const activo = !!value;
	const setDia = (d: number, rangos: [string, string][]) => {
		const dias = { ...h.dias };
		if (rangos.length) dias[d] = rangos; else delete dias[d];
		onChange({ ...h, dias });
	};
	return (
		<div className="tf-horario">
			<label className="cp-check"><input type="checkbox" checked={activo} onChange={(e) => onChange(e.target.checked ? { zona: h.zona, dias: { 1: [["08:00", "18:00"]], 2: [["08:00", "18:00"]], 3: [["08:00", "18:00"]], 4: [["08:00", "18:00"]], 5: [["08:00", "18:00"]], 6: [["08:00", "13:00"]] } } : null)} />
				<b>Solo en horario de atención</b><span className="nu-tenue"> · fuera de él suena el mensaje de fuera de horario</span></label>
			{activo ? (
				<>
					<div className="cp-dos"><div><label>Zona horaria</label><select value={h.zona} onChange={(e) => onChange({ ...h, zona: e.target.value })}>{Object.entries(zonas).map(([k, n]) => <option key={k} value={k}>{n}</option>)}</select></div></div>
					<div className="tf-dias">
						{DIAS_CORTOS.map(([d, t]) => {
							const r = h.dias[d] || [];
							return (
								<div key={d} className={"tf-dia" + (r.length ? " on" : "")}>
									<label className="cp-check"><input type="checkbox" checked={!!r.length} onChange={(e) => setDia(d, e.target.checked ? [["08:00", "18:00"]] : [])} /><b>{t}</b></label>
									{r.map((x, i) => (
										<span key={i} className="tf-rango">
											<input type="time" value={x[0]} onChange={(e) => setDia(d, r.map((y, j) => (j === i ? [e.target.value, y[1]] : y)) as [string, string][])} />–
											<input type="time" value={x[1]} onChange={(e) => setDia(d, r.map((y, j) => (j === i ? [y[0], e.target.value] : y)) as [string, string][])} />
											{r.length > 1 ? <button type="button" className="tf-x" onClick={() => setDia(d, r.filter((_, j) => j !== i) as [string, string][])} aria-label="Quitar rango">×</button> : null}
										</span>
									))}
									{r.length === 1 ? <button type="button" className="nu-btn chico" onClick={() => setDia(d, [...r, ["15:00", "18:00"]] as [string, string][])}>+ otro rango</button> : null}
								</div>
							);
						})}
					</div>
				</>
			) : null}
		</div>
	);
};

export const Check: React.FC<{ checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode }> = ({ checked, onChange, children }) => (
	<label className="cp-check"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />{children}</label>
);
